import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";
import QRCodeStyling from "qr-code-styling";
import { getEpoch } from "../../utils/commonFunctions";
import { useRef, useState } from "react";
import QrTemplate from "../../assets/images/QrNewTemplate.png";

// Template dimensions: see UseCustomQr.jsx for how these were measured.
// QrNewTemplate.png (1618 x 972) is print-ready for a 100mm x 60mm sticker;
// the card/logo/icons/copy are baked into the image, but the QR frame itself
// is blank in the template and drawn dynamically here, same as UseCustomQr.jsx.
const TEMPLATE_WIDTH = 1618;
const TEMPLATE_HEIGHT = 972;
const STICKER_WIDTH_MM = 100;
const STICKER_HEIGHT_MM = 60;
const MM_TO_PT = 72 / 25.4; // pdf-lib page sizes are in points
const STICKER_WIDTH_PT = STICKER_WIDTH_MM * MM_TO_PT;
const STICKER_HEIGHT_PT = STICKER_HEIGHT_MM * MM_TO_PT;

const FRAME_X = 106;
const FRAME_Y = 186;
const FRAME_SIZE = 446;
const QR_X = 150;
const QR_Y = 230;
const QR_SIZE = 358;
const SERIAL_CENTER_X = 329;
const SERIAL_BASELINE_Y = 667;

const qrRenderOptions = {
    margin: 0,
    // Square modules/corners to match the client's reference design exactly.
    qrOptions: { errorCorrectionLevel: "L" },
    dotsOptions: { color: "#000000", type: "square" },
    cornersSquareOptions: { color: "#000000", type: "square" },
    cornersDotOptions: { color: "#000000", type: "square" },
};

const getLayout = (scaleFactor) => ({
    frameX: FRAME_X * scaleFactor,
    frameY: FRAME_Y * scaleFactor,
    frameSize: FRAME_SIZE * scaleFactor,
    qrX: QR_X * scaleFactor,
    qrY: QR_Y * scaleFactor,
    qrSize: Math.round(QR_SIZE * scaleFactor),
    textX: SERIAL_CENTER_X * scaleFactor,
    textY: SERIAL_BASELINE_Y * scaleFactor,
});

// Matches UseCustomQr.jsx's drawCleanQrFrame -- the template's QR area is
// blank, so the frame border is drawn here rather than baked into the image.
const drawFrame = (ctx, layout, scaleFactor) => {
    ctx.save();
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 3 * scaleFactor;
    ctx.strokeRect(layout.frameX + 0.5, layout.frameY + 0.5, layout.frameSize - 1, layout.frameSize - 1);
    ctx.restore();
};

const drawSerial = (ctx, serialText, layout, scaleFactor) => {
    ctx.font = `bold ${20 * scaleFactor}px Arial`;
    ctx.fillStyle = "#000000";
    ctx.textAlign = "center";
    ctx.fillText(serialText, layout.textX, layout.textY);
};

const loadImage = (source) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = source;
    });

const buildStickerDataUrl = async (item, scaleFactor, templateImage) => {
    const layout = getLayout(scaleFactor);
    const qrCode = new QRCodeStyling({
        ...qrRenderOptions,
        width: Math.round(layout.qrSize),
        height: Math.round(layout.qrSize),
        data: item.qr,
    });

    // getRawData already awaits qr-code-styling's internal canvas-drawing
    // promise, so no artificial delay is needed before calling it. This
    // matters a lot here: a range download runs this serially per item.
    const rawData = await qrCode.getRawData("png");
    const qrUrl = URL.createObjectURL(rawData);

    try {
        const qrImage = await loadImage(qrUrl);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(TEMPLATE_WIDTH * scaleFactor);
        canvas.height = Math.round(TEMPLATE_HEIGHT * scaleFactor);

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);
        drawFrame(ctx, layout, scaleFactor);
        ctx.drawImage(qrImage, layout.qrX, layout.qrY, layout.qrSize, layout.qrSize);
        drawSerial(ctx, item.srNo, layout, scaleFactor);

        return canvas.toDataURL("image/png");
    } finally {
        URL.revokeObjectURL(qrUrl);
    }
};

const dataUrlToBytes = (dataUrl) => {
    const binary = atob(dataUrl.split(",")[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
};

const updateProgress = (progress, setProgress) => {
    const roundedProgress = Math.round(progress);
    setProgress((prevProgress) => (prevProgress !== roundedProgress ? roundedProgress : prevProgress));
};

export default function useCustomQRs() {
    const [creationProgress, setCreationProgress] = useState(0);
    const [zipProgress, setZipProgress] = useState(0);
    const cancelledRef = useRef(false);

    // Lets the progress dialog's close (X) button actually stop a running
    // bulk/range generation instead of just hiding a dialog that reopens on
    // the next progress tick.
    const cancelDownload = () => {
        cancelledRef.current = true;
        setCreationProgress(0);
        setZipProgress(0);
    };

    const OnDownload = async (data) => {
        if (!data.length) return;
        cancelledRef.current = false;

        const zip = new JSZip();
        setCreationProgress(0);
        setZipProgress(0);

        const templateImage = await loadImage(QrTemplate);
        const totalItems = data.length;

        for (let index = 0; index < totalItems; index++) {
            if (cancelledRef.current) return;
            const item = data[index];
            // sf = 1: template is already native 300 DPI / 100mm x 60mm.
            const stickerDataUrl = await buildStickerDataUrl(item, 1, templateImage);
            const base64Data = stickerDataUrl.split(",")[1];
            zip.file(`${item.srNo}.png`, base64Data, { base64: true });
            updateProgress(((index + 1) / totalItems) * 100, setCreationProgress);
        }
        if (cancelledRef.current) return;

        const content = await zip.generateAsync({ type: "blob" }, (metadata) => {
            updateProgress(metadata.percent, setZipProgress);
        });
        if (cancelledRef.current) return;

        const link = document.createElement("a");
        link.href = URL.createObjectURL(content);
        link.download = `${getEpoch()}.zip`;
        link.click();

        setCreationProgress(0);
        setZipProgress(0);
    };

    // Selected QR codes -> one PDF, one sticker per page at its real print size.
    const OnDownloadPNG = async (data) => {
        if (!data.length) return;
        cancelledRef.current = false;
        try {
            setCreationProgress(0);
            const templateImage = await loadImage(QrTemplate);
            const pdfDoc = await PDFDocument.create();

            for (let index = 0; index < data.length; index++) {
                if (cancelledRef.current) return;
                const item = data[index];
                // sf = 1: template is already native 300 DPI / 100mm x 60mm.
                const stickerDataUrl = await buildStickerDataUrl(item, 1, templateImage);
                const pngImage = await pdfDoc.embedPng(dataUrlToBytes(stickerDataUrl));
                const page = pdfDoc.addPage([STICKER_WIDTH_PT, STICKER_HEIGHT_PT]);
                page.drawImage(pngImage, { x: 0, y: 0, width: STICKER_WIDTH_PT, height: STICKER_HEIGHT_PT });
                updateProgress(((index + 1) / data.length) * 100, setCreationProgress);
            }
            if (cancelledRef.current) return;

            const pdfBytes = await pdfDoc.save();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([pdfBytes], { type: "application/pdf" }));
            link.download = `${getEpoch()}.pdf`;
            link.click();
        } catch (error) {
            console.error("Error downloading QR codes:", error);
        } finally {
            setCreationProgress(0);
        }
    };

    return { creationProgress, zipProgress, OnDownload, OnDownloadPNG, cancelDownload };
}
